import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Paper,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

interface TermsOfUseProps {
  open: boolean;
  onClose: () => void;
}

const TermsOfUse: React.FC<TermsOfUseProps> = ({ open, onClose }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 1,
        borderBottom: '1px solid #eee'
      }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
          Terms of Use
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

             <DialogContent sx={{ py: 3 }}>
         <Box sx={{ maxWidth: '100%' }}>
           <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
             <strong>Effective Date:</strong> 1 June 2025
           </Typography>

           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             These Terms govern your access to and use of Gamura. By using the service, you agree to comply with these Terms.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             1. Use of the Service
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • You may upload documents and ask questions for personal or organizational use.
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • You must not use the platform for unlawful, harmful, or misleading purposes.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • You are responsible for the legality and ownership of uploaded content.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             2. Account Responsibility
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • Keep your login credentials secure.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • You are responsible for any activity under your account.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             3. Intellectual Property
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • You own your documents and queries.
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • Gamura owns the platform, tools, and underlying AI infrastructure.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • You may not reverse-engineer or resell the service.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             4. Service Availability
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • We strive to maintain uptime but do not guarantee uninterrupted service.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • Maintenance or technical issues may result in brief downtimes.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             5. Liability Disclaimer
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • The service is provided "as is" with no warranties.
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • We are not responsible for losses resulting from reliance on AI-generated answers.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • Always verify critical information manually.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             6. Termination
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • We reserve the right to suspend or terminate access if terms are violated.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • You may discontinue use at any time.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             7. Changes to Terms
           </Typography>
           <Typography variant="body2" sx={{ mb: 2, color: '#444', lineHeight: 1.6 }}>
             • We may update these Terms with reasonable notice.
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             • Continued use of Gamura after updates indicates acceptance.
           </Typography>

           <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#1a1a1a' }}>
             8. Contact Us
           </Typography>
           <Typography variant="body2" sx={{ mb: 3, color: '#444', lineHeight: 1.6 }}>
             For any questions or concerns, email:
           </Typography>
           <Paper sx={{ p: 2, bgcolor: '#f8f9fa', border: '1px solid #e0e0e0' }}>
             <Typography variant="body2" sx={{ color: '#444' }}>
               <strong>Email:</strong> support@gamura.ai
             </Typography>
           </Paper>
         </Box>
       </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #eee' }}>
        <Button 
          onClick={onClose} 
          variant="contained"
          sx={{ 
            bgcolor: '#1a1a1a',
            color: '#fff',
            textTransform: 'none',
            fontWeight: 500,
            '&:hover': {
              bgcolor: '#333',
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TermsOfUse; 